import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Bell,
  Store,
  Building2,
  Receipt,
  AlertTriangle,
  LifeBuoy,
  CheckCheck,
  type LucideIcon,
} from 'lucide-react'
import type { AppNotification, NotificationType } from './types'
import { useNotifications } from './useNotifications'

const TYPE_STYLE: Record<NotificationType, { icon: LucideIcon; wrap: string }> = {
  business_change: { icon: Store, wrap: 'bg-info-soft text-info' },
  org_change: { icon: Building2, wrap: 'bg-info-soft text-info' },
  invoice_generated: { icon: Receipt, wrap: 'bg-success-soft text-success' },
  invoice_pending: { icon: AlertTriangle, wrap: 'bg-warning-soft text-warning' },
  ticket_update: { icon: LifeBuoy, wrap: 'bg-brand-soft text-brand' },
}

export function NotificationsBell() {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const { items, unread, markAllRead, markRead } = useNotifications()

  const onItemClick = (n: AppNotification) => {
    markRead(n.id)
    setOpen(false)
    if (n.href) navigate(n.href)
  }

  return (
    <div className="relative">
      <button
        type="button"
        aria-label="Notificaciones"
        onClick={() => setOpen((v) => !v)}
        className="relative grid h-[38px] w-[38px] place-items-center rounded-lg border border-line bg-panel text-ink-muted hover:text-ink"
      >
        <Bell className="h-[18px] w-[18px]" />
        {unread > 0 && (
          <span className="absolute -right-1 -top-1 grid h-[18px] min-w-[18px] place-items-center rounded-full border-2 border-panel bg-danger px-1 text-[10px] font-bold leading-none text-white">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* Backdrop para cerrar al hacer clic fuera */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />

          <div className="absolute right-0 z-50 mt-2 w-[min(360px,calc(100vw-2rem))] overflow-hidden rounded-card border border-line bg-panel shadow-xl">
            <div className="flex items-center justify-between border-b border-line px-4 py-3">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-bold text-ink">Notificaciones</h3>
                {unread > 0 && (
                  <span className="rounded-full bg-danger-soft px-1.5 py-0.5 text-[10px] font-bold text-danger">
                    {unread} sin leer
                  </span>
                )}
              </div>
              {unread > 0 && (
                <button
                  onClick={markAllRead}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-brand hover:underline"
                >
                  <CheckCheck className="h-3.5 w-3.5" /> Marcar leídas
                </button>
              )}
            </div>

            {items.length === 0 ? (
              <p className="px-4 py-10 text-center text-sm text-ink-muted">No tienes notificaciones.</p>
            ) : (
              <ul className="max-h-[min(420px,60vh)] divide-y divide-line overflow-y-auto">
                {items.map((n) => {
                  const { icon: Icon, wrap } = TYPE_STYLE[n.type]
                  return (
                    <li key={n.id}>
                      <button
                        onClick={() => onItemClick(n)}
                        className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-surface ${
                          n.read ? '' : 'bg-brand-soft/40'
                        }`}
                      >
                        <span className={`mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg ${wrap}`}>
                          <Icon className="h-4 w-4" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-2">
                            <span className="truncate text-sm font-semibold text-ink">{n.title}</span>
                            {!n.read && <span className="h-2 w-2 shrink-0 rounded-full bg-brand" />}
                          </span>
                          <span className="mt-0.5 block text-xs text-ink-muted">{n.description}</span>
                          <span className="mt-1 block text-[11px] text-ink-subtle">{n.time}</span>
                        </span>
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  )
}
